import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../styles/Analytics.css';
import AuthData from '../common/AuthData';
import { ANALYTICS_API } from '../common/ResourcePaths';
import RelativeSvgLoader from '../common/components/RelativeSvgLoader';
import ExportModal from '../common/components/ExportModal';
import { getStatusColor } from '../common/utils/statusUtils';
import html2canvas from 'html2canvas';

const Analytics = () => {
    const analyticsRef = useRef(null);
    const [dateRange, setDateRange] = useState('last30days');
    const [statusFilter, setStatusFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [customDateFrom, setCustomDateFrom] = useState('');
    const [customDateTo, setCustomDateTo] = useState('');
    const [analyticsData, setAnalyticsData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [exportingImage, setExportingImage] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportFileName, setExportFileName] = useState('');

    // Show export modal
    const showExportDialog = () => {
        const defaultName = `analytics-${dateRange}-${new Date().toISOString().split('T')[0]}`;
        setExportFileName(defaultName);
        setShowExportModal(true);
    };

    // Export view as image
    const exportAsImage = async () => {
        if (!analyticsRef.current) return;
        
        const fileName = exportFileName.trim() || `analytics-${dateRange}-${new Date().toISOString().split('T')[0]}`;
        
        setShowExportModal(false);
        setExportingImage(true);
        
        try {
            const canvas = await html2canvas(analyticsRef.current, {
                backgroundColor: '#ffffff',
                scale: 2,
                logging: false,
                useCORS: true
            });
            
            const link = document.createElement('a');
            link.download = `${fileName}.jpg`;
            link.href = canvas.toDataURL('image/jpeg', 0.95);
            link.click();
        } catch (error) {
            console.error('Error exporting image:', error);
            alert('Failed to export image. Please try again.');
        } finally {
            setExportingImage(false);
        }
    };

    // Restaurant service configuration
    const serviceConfig = {
        name: 'Restaurant',
        metrics: ['Revenue', 'Orders', 'Customers', 'Avg Order Value'],
        categories: ['Appetizers', 'Main Course', 'Desserts', 'Beverages', 'Specials'],
        statusTypes: ['completed', 'preparing', 'pending', 'cancelled']
    };

    // Get dynamic categories from analytics data
    const getAvailableCategories = () => {
        // Use allCategories from backend if available, otherwise extract from topCategories
        if (analyticsData?.allCategories && analyticsData.allCategories.length > 0) {
            return analyticsData.allCategories;
        }
        if (analyticsData?.topCategories && analyticsData.topCategories.length > 0) {
            return analyticsData.topCategories.map(cat => cat.name);
        }
        return serviceConfig.categories; // Fallback to static categories
    };

    // Get dynamic statuses from analytics data  
    const getAvailableStatuses = () => {
        // Priority 1: Use allStatuses from backend if available
        if (analyticsData?.allStatuses && analyticsData.allStatuses.length > 0) {
            return analyticsData.allStatuses;
        } 
        
        // Priority 2: Extract from statusDistribution
        if (analyticsData?.statusDistribution && Object.keys(analyticsData.statusDistribution).length > 0) {
            const statusKeys = Object.keys(analyticsData.statusDistribution);
            return statusKeys;
        } 
        
        // Fallback: Use static statuses
        return serviceConfig.statusTypes;
    };

    // Get date range filter for API
    const getDateRangeFilter = useCallback(() => {
        const now = new Date();
        let startDate = new Date(now);
        let endDate = new Date(now);

        switch (dateRange) {
            case 'today':
                startDate.setHours(0, 0, 0, 0);
                endDate.setHours(23, 59, 59, 999);
                // Don't restrict endDate for today - we want the full day
                break;
            case 'yesterday':
                startDate.setDate(startDate.getDate() - 1);
                startDate.setHours(0, 0, 0, 0);
                endDate.setDate(endDate.getDate() - 1);
                endDate.setHours(23, 59, 59, 999);
                break;
            case 'last7days':
                startDate.setDate(startDate.getDate() - 7);
                startDate.setHours(0, 0, 0, 0);
                endDate.setHours(23, 59, 59, 999);
                break;
            case 'last30days':
                startDate.setDate(startDate.getDate() - 30);
                startDate.setHours(0, 0, 0, 0);
                endDate.setHours(23, 59, 59, 999);
                break;
            case 'last3months':
                startDate.setMonth(startDate.getMonth() - 3);
                startDate.setHours(0, 0, 0, 0);
                endDate.setHours(23, 59, 59, 999);
                break;
            case 'last6months':
                startDate.setMonth(startDate.getMonth() - 6);
                startDate.setHours(0, 0, 0, 0);
                endDate.setHours(23, 59, 59, 999);
                break;
            case 'lastyear':
                startDate.setFullYear(startDate.getFullYear() - 1);
                startDate.setHours(0, 0, 0, 0);
                endDate.setHours(23, 59, 59, 999);
                break;
            case 'custom':
                startDate = customDateFrom ? new Date(customDateFrom) : new Date();
                endDate = customDateTo ? new Date(customDateTo) : new Date();
                break;
            default:
                startDate = new Date();
                startDate.setDate(startDate.getDate() - 30);
        }

        // Return ISO strings instead of Date objects for API compatibility
        return { 
            startDate: startDate.toISOString(), 
            endDate: endDate.toISOString() 
        };
    }, [dateRange, customDateFrom, customDateTo]);

    // Transform backend data to frontend format
    const transformAnalyticsData = useCallback((backendData) => {
        // Convert backend OrdersByStatus to frontend format
        const statusDistribution = {};
        backendData.ordersByStatus?.forEach(item => {
            // Replace InTransit with Delivering for display
            const displayStatus = item.status === 'InTransit' ? 'Delivering' : item.status;
            statusDistribution[displayStatus] = item.count;
        });
        
        // Convert backend OrdersByDeliveryType to frontend format
        const deliveryTypeDistribution = {};
        if (backendData.ordersByDeliveryType && Array.isArray(backendData.ordersByDeliveryType)) {
            backendData.ordersByDeliveryType.forEach(item => {
                deliveryTypeDistribution[item.deliveryType || item.type] = item.count;
            });
        }

        // Convert backend OrdersByPaymentMethod to frontend format
        const paymentTypeDistribution = {};
        if (backendData.ordersByPaymentMethod && Array.isArray(backendData.ordersByPaymentMethod)) {
            backendData.ordersByPaymentMethod.forEach(item => {
                // Map backend payment method names to display names (handle different case variations)
                const paymentMethod = item.paymentMethod.toLowerCase();
                let displayName = item.paymentMethod;
                
                if (paymentMethod === 'cardonline') {
                    displayName = 'Card Online';
                } else if (paymentMethod === 'cash') {
                    displayName = 'Cash';
                } else if (paymentMethod === 'cardondelivery') {
                    displayName = 'Card On Delivery';
                }
                
                paymentTypeDistribution[displayName] = item.count;
            });
        }

        const categoryDistribution = {};
        if (backendData.ordersByCategory && Array.isArray(backendData.ordersByCategory)) {
            backendData.ordersByCategory.forEach(item => {
                categoryDistribution[item.category] = item.count;
            });
        }

        // Transform allStatuses array to replace InTransit with Delivering
        const transformedStatuses = backendData.allStatuses?.map(status => 
            status === 'InTransit' ? 'Delivering' : status
        ) || [];

        // Convert backend OrdersByCity to frontend format
        const cityDistribution = [];
        if (backendData.ordersByCity && Array.isArray(backendData.ordersByCity)) {
            const totalOrders = backendData.ordersByCity.reduce((sum, item) => sum + item.count, 0);
            cityDistribution.push(...backendData.ordersByCity.map(item => ({
                name: item.city,
                value: item.count,
                percentage: totalOrders > 0 ? parseFloat(((item.count / totalOrders) * 100).toFixed(1)) : 0,
                growth: 0 // Backend doesn't provide growth data yet
            })));
        }

        // Generate missing data that backend doesn't provide yet
        const transformedData = {
            totalRevenue: backendData.revenue || 0,
            totalTransactions: backendData.numberOfOrders || 0,
            activeClients: backendData.numberOfCustomers || 0,
            performanceRate: backendData.averageOrderValue || 0,
            statusDistribution,
            allCategories: backendData.allCategories || [],
            allStatuses: transformedStatuses,
            deliveryTypeDistribution,
            paymentTypeDistribution,
            categoryDistribution,
            cityDistribution,
            topCategories: backendData.topCategories?.map(cat => ({
                name: cat.category,
                value: cat.orderCount,
                growth: Math.random() * 20 - 10 // Random growth for now
            })) || []
        };
        
        return transformedData;
    }, []);

    // Fetch analytics data from backend
    const fetchAnalyticsData = async () => {
        try {
            setIsLoading(true);
            setError(null);
            
            const authData = AuthData.getAuthData();
            if (!authData || !authData.appFunctionUrl) {
                throw new Error('Authentication data not available');
            }

            // Prepare filter based on current selections
            const filter = {
                dateRange: getDateRangeFilter()
            };

            // Always add status filter if not 'all'
            if (statusFilter !== 'all') {
                filter.status = statusFilter;
            }

            // Always add category filter if not 'all'
            if (categoryFilter !== 'all') {
                filter.category = categoryFilter;
            }

            const url = `${authData.appFunctionUrl}${ANALYTICS_API}?code=${authData.azureFunctionKey}`;
            
            // Make direct fetch call since we need POST method
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'clientId': authData.clientId,
                    'idToken': localStorage.getItem('user'),
                    'x-platform': 'true'
                },
                body: JSON.stringify(filter)
            });

            if (response.status === 401) {
                console.error('🚫 UNAUTHORIZED - Redirecting...');
                localStorage.removeItem('user');
                window.location.href = '/unauthorized';
                return;
            }

            if (!response.ok) {
                // Try to get the actual error message from the response
                let errorMessage = `HTTP error! status: ${response.status}`;
                try {
                    const errorData = await response.text();
                    if (errorData) {
                        try {
                            const parsedError = JSON.parse(errorData);
                            errorMessage += ` - ${parsedError.message || parsedError.error || errorData}`;
                        } catch (e) {
                            errorMessage += ` - ${errorData}`;
                        }
                    }
                } catch (e) {
                    console.error('❌ Could not read error response:', e);
                }
                
                throw new Error(errorMessage);
            }

            const responseData = await response.json();

            if (responseData && responseData.data) {
                // Transform backend data to match frontend structure
                const transformedData = transformAnalyticsData(responseData.data);
                setAnalyticsData(transformedData);
            } else {
                throw new Error('Invalid response format');
            }
        } catch (error) {
            console.error('Error fetching analytics data:', error);
            setError(error.message);
            // Set empty data structure instead of fallback
            setAnalyticsData({
                totalRevenue: 0,
                totalTransactions: 0,
                activeClients: 0,
                performanceRate: 0,
                statusDistribution: {},
                deliveryTypeDistribution: {},
                paymentTypeDistribution: {},
                categoryDistribution: {},
                cityDistribution: [],
                allCategories: [],
                allStatuses: []
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Manual function to apply filters and fetch data
    const applyFilters = () => {
        fetchAnalyticsData();
    };

    // Fetch data only once on component mount
    useEffect(() => {
        fetchAnalyticsData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Advanced Chart Components

    const BarChart = ({ data, title }) => {
        const maxValue = Math.max(...data.map(item => item.value));
        const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

        return (
            <div className="advanced-chart-container">
                <h3 className="chart-title">{title}</h3>
                <div className="bar-chart-container">
                    {data.map((item, index) => (
                        <div key={item.name} className="bar-item">
                            <div className="bar-header">
                                <span className="bar-name">{item.name}</span>
                                <span className="bar-stats">
                                    <span className="bar-value">{item.value}</span>
                                    <span className="bar-percentage">({item.percentage}%)</span>
                                </span>
                            </div>
                            <div className="bar-container">
                                <div 
                                    className="bar-fill"
                                    style={{
                                        width: `${(item.value / maxValue) * 100}%`,
                                        backgroundColor: colors[index % colors.length]
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const PieChart = ({ data, title }) => {
        const total = Object.values(data).reduce((sum, value) => sum + value, 0);
        const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
        let currentAngle = 0;
        
        const createSlicePath = (centerX, centerY, radius, startAngle, endAngle) => {
            const start = {
                x: centerX + radius * Math.cos((startAngle * Math.PI) / 180),
                y: centerY + radius * Math.sin((startAngle * Math.PI) / 180)
            };
            const end = {
                x: centerX + radius * Math.cos((endAngle * Math.PI) / 180),
                y: centerY + radius * Math.sin((endAngle * Math.PI) / 180)
            };
            
            const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
            
            return [
                "M", centerX, centerY,
                "L", start.x, start.y,
                "A", radius, radius, 0, largeArcFlag, 1, end.x, end.y,
                "Z"
            ].join(" ");
        };

        return (
            <div className="advanced-chart-container">
                <h3 className="chart-title">{title}</h3>
                <div className="pie-chart-container">
                    <svg width="240" height="240" viewBox="0 0 240 240">
                        {Object.entries(data).map(([key, value], index) => {
                            if (total === 0) return null;
                            const sliceAngle = (value / total) * 360;
                            
                            // Handle single item case
                            if (Object.keys(data).length === 1) {
                                return <circle key={key} cx="120" cy="120" r="80" fill={colors[0]} />;
                            }

                            const path = createSlicePath(120, 120, 80, currentAngle, currentAngle + sliceAngle);
                            currentAngle += sliceAngle;

                            return (
                                <g key={key}>
                                    <path
                                        d={path}
                                        fill={colors[index % colors.length]}
                                        stroke="#ffffff"
                                        strokeWidth="2"
                                        className="pie-slice"
                                    >
                                      <title>{`${key}: ${value}`}</title>
                                    </path>
                                </g>
                            );
                        })}
                    </svg>
                    <div className="pie-legend">
                        {Object.entries(data).map(([key, value], index) => {
                            const percentage = ((value / total) * 100).toFixed(1);
                            return (
                                <div key={key} className="legend-item">
                                    <div 
                                        className="legend-color" 
                                        style={{ backgroundColor: colors[index % colors.length] }}
                                    />
                                    <span className="legend-label">{key}</span>
                                    <span className="legend-value">{value} ({percentage}%)</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };


    const DonutChart = ({ data, title }) => {
        const total = Object.values(data).reduce((sum, value) => sum + value, 0);
        let currentAngle = 0;
        const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
        const isEmpty = Object.keys(data).length === 0 || total === 0;

        return (
            <div className="advanced-chart-container">
                <h3 className="chart-title">{title}</h3>
                <div className="donut-chart-container">
                    <svg width="200" height="200" viewBox="0 0 100 100">
                        {isEmpty ? (
                            // Empty state - show grey donut
                            <g>
                                <circle
                                    cx="50"
                                    cy="50"
                                    r="35"
                                    fill="#e5e7eb"
                                />
                                <circle
                                    cx="50"
                                    cy="50"
                                    r="20"
                                    fill="white"
                                />
                            </g>
                        ) : Object.entries(data).length === 1 ? (
                            // Special case for single item - draw a full donut
                            Object.entries(data).map(([key, value]) => {
                                const isStatusChart = title.toLowerCase().includes('status');
                                const segmentColor = isStatusChart ? getStatusColor(key) : colors[0];
                                
                                return (
                                    <g key={key}>
                                        <circle
                                            cx="50"
                                            cy="50"
                                            r="35"
                                            fill={segmentColor}
                                        />
                                        <circle
                                            cx="50"
                                            cy="50"
                                            r="20"
                                            fill="white"
                                        />
                                    </g>
                                );
                            })
                        ) : (
                            // Multiple items - draw path segments
                            Object.entries(data).map(([key, value], index) => {
                                const percentage = (value / total) * 100;
                                const angle = (percentage / 100) * 360;
                                const startAngle = currentAngle;
                                const endAngle = currentAngle + angle;
                                currentAngle += angle;

                                const isStatusChart = title.toLowerCase().includes('status');
                                const segmentColor = isStatusChart ? getStatusColor(key) : colors[index % colors.length];

                                // Convert to radians and adjust for SVG coordinate system
                                const startRad = ((startAngle - 90) * Math.PI) / 180;
                                const endRad = ((endAngle - 90) * Math.PI) / 180;
                                
                                // Calculate coordinates for outer and inner arcs
                                const outerRadius = 35;
                                const innerRadius = 20;
                                
                                const x1 = 50 + outerRadius * Math.cos(startRad);
                                const y1 = 50 + outerRadius * Math.sin(startRad);
                                const x2 = 50 + outerRadius * Math.cos(endRad);
                                const y2 = 50 + outerRadius * Math.sin(endRad);
                                
                                const x3 = 50 + innerRadius * Math.cos(endRad);
                                const y3 = 50 + innerRadius * Math.sin(endRad);
                                const x4 = 50 + innerRadius * Math.cos(startRad);
                                const y4 = 50 + innerRadius * Math.sin(startRad);
                                
                                const largeArc = angle > 180 ? 1 : 0;
                                
                                const pathData = [
                                    `M ${x1} ${y1}`,
                                    `A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2} ${y2}`,
                                    `L ${x3} ${y3}`,
                                    `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4}`,
                                    'Z'
                                ].join(' ');

                                return (
                                    <path
                                        key={key}
                                        d={pathData}
                                        fill={segmentColor}
                                        stroke="#ffffff"
                                        strokeWidth="1"
                                    >
                                      <title>{`${key}: ${value}`}</title>
                                    </path>
                                );
                            })
                        )}
                        <text x="50" y="45" textAnchor="middle" className="donut-total-label">Total</text>
                        <text x="50" y="55" textAnchor="middle" className="donut-total-value">{total}</text>
                    </svg>
                    <div className="donut-legend">
                        {isEmpty ? (
                            <div className="donut-legend-item" style={{ color: '#9ca3af' }}>
                                <span className="legend-text">No data available</span>
                            </div>
                        ) : (
                            Object.entries(data).map(([key, value], index) => (
                                <div key={key} className="donut-legend-item">
                                    <span 
                                        className="legend-color" 
                                        style={{ backgroundColor: title.toLowerCase().includes('status') ? getStatusColor(key) : colors[index % colors.length] }}
                                    />
                                    <span className="legend-text">{key}: {value}</span>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="analytics" style={{ position: 'relative' }}>
            {isLoading && <RelativeSvgLoader />}
            
            {/* Export Modal */}
            <ExportModal
                show={showExportModal}
                fileName={exportFileName}
                onFileNameChange={setExportFileName}
                onExport={exportAsImage}
                onCancel={() => setShowExportModal(false)}
            />
            
            {error && (
                <div className="error-message" style={{ 
                    padding: '20px', 
                    backgroundColor: '#fee2e2', 
                    border: '1px solid #fecaca', 
                    borderRadius: '8px', 
                    margin: '20px', 
                    color: '#dc2626' 
                }}>
                    <strong>Error loading analytics data:</strong> {error}
                    <br />
                    <small>Displaying fallback data instead.</small>
                </div>
            )}
            
            <div ref={analyticsRef} style={{ opacity: isLoading ? 0.3 : 1 }}>
            {/* Advanced Filters Panel */}
            <div className="filters-panel">
                <div className="filters-row">
                    <div className="filter-group">
                        <label>📅 Date Range</label>
                        <select 
                            value={dateRange} 
                            onChange={(e) => setDateRange(e.target.value)}
                            className="filter-select"
                        >
                            <option value="today">Today</option>
                            <option value="yesterday">Yesterday</option>
                            <option value="last7days">Last 7 Days</option>
                            <option value="last30days">Last 30 Days</option>
                            <option value="last3months">Last 3 Months</option>
                            <option value="last6months">Last 6 Months</option>
                            <option value="lastyear">Last Year</option>
                            <option value="custom">Custom Range</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>📊 Status</label>
                        <select 
                            value={statusFilter} 
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="filter-select"
                        >
                            <option value="all">All Status</option>
                            {getAvailableStatuses().map(status => (
                                <option key={status} value={status}>
                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>🏷️ Category</label>
                        <select 
                            value={categoryFilter} 
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="filter-select"
                        >
                            <option value="all">All Categories</option>
                            {getAvailableCategories().map(category => (
                                <option key={category} value={category}>{category}</option>
                            ))}
                        </select>
                    </div>

                    <button 
                        className="filter-action-btn primary"
                        onClick={applyFilters}
                        disabled={isLoading}
                        style={{ minWidth: '120px' }}
                    >
                        Apply Filters
                    </button>

                    <button 
                        className="filter-action-btn export"
                        onClick={showExportDialog}
                        disabled={exportingImage || isLoading}
                        style={{ minWidth: '120px' }}
                    >
                        {exportingImage ? '⏳ Exporting...' : '📊 Export Data'}
                    </button>
                </div>

                {dateRange === 'custom' && (
                    <div className="custom-date-range" style={{ marginTop: '15px' }}>
                        <div className="filter-group">
                            <label>From Date & Time</label>
                            <input 
                                type="datetime-local" 
                                value={customDateFrom}
                                onChange={(e) => setCustomDateFrom(e.target.value)}
                                className="date-input"
                            />
                        </div>
                        <div className="filter-group">
                            <label>To Date & Time</label>
                            <input 
                                type="datetime-local" 
                                value={customDateTo}
                                onChange={(e) => setCustomDateTo(e.target.value)}
                                className="date-input"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Dynamic Key Metrics Cards */}
            {analyticsData && (
                <div className="metrics-grid">
                    {serviceConfig.metrics.map((metric, index) => {
                        const values = [
                            analyticsData.totalRevenue,
                            analyticsData.totalTransactions,
                            analyticsData.activeClients,
                            analyticsData.performanceRate
                        ];
                        const icons = ['💰', '📦', '👥', '📈'];

                        return (
                            <div key={metric} className="metric-card">
                                <div className="metric-icon">{icons[index]}</div>
                                <div className="metric-content">
                                    <h3>{metric}</h3>
                                    <div className="metric-value">
                                        {index === 0 ? 
                                            `$${values[index].toLocaleString()}` : 
                                            index === 3 ? 
                                                `$${Number(values[index]).toFixed(2)}` :
                                                values[index].toLocaleString()
                                        }
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Enhanced Charts Grid */}
            {analyticsData && (
                <div className="charts-grid">
                    <div className="chart-card">
                        <DonutChart 
                            data={analyticsData.statusDistribution} 
                            title="Orders by Status"
                        />
                    </div>
                    
                    <div className="chart-card">
                        <DonutChart 
                            data={analyticsData.deliveryTypeDistribution} 
                            title="Orders by Delivery Type"
                        />
                    </div>
                    
                    <div className="chart-card">
                        <PieChart 
                            data={analyticsData.paymentTypeDistribution} 
                            title="Orders by Payment Type"
                        />
                    </div>

                    <div className="chart-card">
                        <PieChart
                            data={analyticsData.categoryDistribution}
                            title="Orders by Category"
                        />
                    </div>
                    
                    <div className="chart-card wide">
                        <BarChart 
                            data={analyticsData.cityDistribution} 
                            title="Orders by City"
                        />
                    </div>
                </div>
            )}
            </div>

        </div>
    );
};

export default Analytics;